import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { GetServerSidePropsContext } from 'next';
import getConfig from 'next/config';

import { getJwt } from '@/common/cookie/jwt';
import { checkJwt } from '@/common/next/authenticate';
import { TMeta } from '@/types/responses/meta';

export type TLwtApiResponse<T> = { data: T; meta?: TMeta; status: number; isError: boolean };
// APIのErrorHandler#TEachErrorInResponseとisError以外の型を合わせること。
type TEachErrorInResponse = { status: number; code: string; message: string; isError: false };
// APIのErrorHandler#TResponseBodyとisError以外の型を合わせること。
export type TResponseBodyOnError = { errors: TEachErrorInResponse[]; status: number; isError: true };

const { publicRuntimeConfig } = getConfig();
const BASE_URL = `${publicRuntimeConfig.LWT_API_ORIGIN}/api/`;

export class LWTApiClient {
  axiosInstance: AxiosInstance;

  constructor({
    headers = {},
    timeout = 30000,
    context,
  }: {
    headers?: { [key: string]: string };
    timeout?: number;
    context?: GetServerSidePropsContext;
  }) {
    const jwt = getJwt(context);
    if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
    this.axiosInstance = axios.create({ baseURL: BASE_URL, headers, timeout, responseType: 'json' });
    this.axiosInstance.interceptors.request.use(
      async (config: AxiosRequestConfig) => config,
      (err: AxiosError) => Promise.reject(err),
    );
  }

  get = async <T>(path: string, params = {}) => await this.request<T>((axios) => axios.get, path, params);

  post = async <T>(path: string, params = {}) => await this.request<T>((axios) => axios.post, path, params);

  put = async <T>(path: string, params = {}) => await this.request<T>((axios) => axios.put, path, params);

  delete = async <T>(path: string, params = {}) => await this.request<T>((axios) => axios.delete, path, params);

  downloadFile = async (params: {
    path: string;
    fileName: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params?: any;
    isGET?: boolean;
  }): Promise<TLwtApiResponse<null> | TResponseBodyOnError> => {
    try {
      // download file data
      const requestConfig: AxiosRequestConfig = { responseType: 'blob' };
      const response = await (params.isGET
        ? this.axiosInstance.get(params.path, requestConfig)
        : this.axiosInstance.post(params.path, params.params ?? {}, requestConfig));

      // create temp link element
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.setAttribute('download', params.fileName);
      document.body.appendChild(link);

      // convert fetched file data to a local file
      link.click();

      // delete temp element
      document.body.removeChild(link);

      return Object.assign(response.data, { isError: false });
    } catch (e) {
      return Object.assign(e.response?.data || {}, { status: e.response?.status, isError: true });
    }
  };

  private request = async <T>(
    // eslint-disable-next-line @typescript-eslint/ban-types
    verb: (axiosInstance: AxiosInstance) => Function,
    path: string,
    params = {},
  ): Promise<TLwtApiResponse<T> | TResponseBodyOnError> => {
    if (!checkJwt()) {
      return { data: {} as T, status: 200, isError: false };
    }
    try {
      return Object.assign((await verb(this.axiosInstance)(path, params)).data, { isError: false });
    } catch (e) {
      return Object.assign(e.response?.data || {}, { status: e.response?.status, isError: true });
    }
  };
}

export const getHeaderForLoginAuthentication = (context?: GetServerSidePropsContext) => {
  const jwt = getJwt(context);
  return jwt ? { Authorization: `Bearer ${jwt}` } : undefined;
};
